import React, { useMemo, useState } from "react";
import styled from "styled-components";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { toast } from "react-hot-toast";
import { colors } from "../../../Shared/colors";
import {
  useGetActiveStaffQuery,
  useHierarchyCreateMutation,
  useHierarchyUpdateMutation,
} from "../../../features/employeeSlice";
import { Card, CardHead, PrimaryButton } from "../staffStyles";

/**
 * Add or update a place in the team structure.
 *
 * This replaces TeamHerarchyFirst and TeamHerarchyFirst2, which were the same
 * 237-line form copied twice — one calling create, the other update. Copies
 * drift: any fix to the form had to be made twice, and whoever made it second
 * had to notice the first existed.
 *
 * ── The one genuine difference, which must not be "tidied away" ──────────────
 * The two endpoints disagree on the payload key:
 *
 *     POST /hierarchical/create   expects  subordinatesIds   (with the s)
 *     PUT  /hierarchical/update   expects  subordinateIds    (without)
 *
 * Verified against hierarchicalController.js:11 and :188. Standardising on one
 * key here would silently stop subordinates being saved on whichever endpoint
 * lost — no error, the request succeeds and the team is quietly empty. The key
 * is therefore chosen by mode below. Remove this branch only after the API
 * accepts both spellings.
 */
const SUBORDINATE_KEY = {
  create: "subordinatesIds",
  update: "subordinateIds",
};

export const HierarchyForm = ({ mode = "create" }) => {
  const isCreate = mode === "create";

  const { data: employee } = useGetActiveStaffQuery();
  const [createHierarchy, { isLoading: isCreating }] = useHierarchyCreateMutation();
  const [updateHierarchy, { isLoading: isUpdating }] = useHierarchyUpdateMutation();

  const isSaving = isCreate ? isCreating : isUpdating;

  const [teamData, setTeamData] = useState({
    employeeId: "",
    managerId: "",
    subordinates: [],
  });

  const reset = () =>
    setTeamData({ employeeId: "", managerId: "", subordinates: [] });

  const handleChange = (e) =>
    setTeamData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const options = useMemo(
    () =>
      (employee || []).map((emp) => ({
        id: emp._id,
        name: `${emp.firstName} ${emp.lastName}`,
        role: emp.role,
      })),
    [employee]
  );

  // The employee being placed cannot also be their own manager or their own
  // subordinate — the original form allowed both, which produces a node that
  // points at itself and breaks the tree view.
  const selectable = useMemo(
    () => options.filter((o) => o.id !== teamData.employeeId),
    [options, teamData.employeeId]
  );

  const handleSave = async (e) => {
    e.preventDefault();

    const subordinateIds = teamData.subordinates.map((s) => s.id);

    // Only send what was actually filled in. The endpoints treat an empty
    // manager and an empty subordinate list as "leave this part alone", so
    // sending empty values would clear an existing relationship.
    const payload = { employeeId: teamData.employeeId };
    if (teamData.managerId) payload.managerId = teamData.managerId;
    if (subordinateIds.length) payload[SUBORDINATE_KEY[mode]] = subordinateIds;

    try {
      const run = isCreate ? createHierarchy : updateHierarchy;
      await run({ data: payload }).unwrap();
      toast.success(isCreate ? "Hierarchy created" : "Hierarchy updated");
      reset();
    } catch (err) {
      toast.error(
        err?.data?.message ||
          err?.data?.error?.message ||
          `Couldn't ${isCreate ? "create" : "update"} the hierarchy`
      );
      // Deliberately not resetting on failure: the original cleared the form on
      // error too, so a mistyped entry meant filling everything in again.
    }
  };

  const canSave =
    Boolean(teamData.employeeId) &&
    (Boolean(teamData.managerId) || teamData.subordinates.length > 0);

  return (
    <Card as="form" onSubmit={handleSave}>
      <CardHead>
        <span className="count">
          {isCreate ? "Add to the team structure" : "Update the team structure"}
        </span>
      </CardHead>

      <Body>
        <Row>
          <FieldBlock>
            <label htmlFor={`${mode}-employee`}>Employee</label>
            <select
              id={`${mode}-employee`}
              name="employeeId"
              value={teamData.employeeId}
              onChange={handleChange}
            >
              <option value="">Select an employee</option>
              {options.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name} — {o.role}
                </option>
              ))}
            </select>
          </FieldBlock>

          <FieldBlock>
            <label htmlFor={`${mode}-manager`}>Reports to</label>
            <select
              id={`${mode}-manager`}
              name="managerId"
              value={teamData.managerId}
              onChange={handleChange}
              disabled={!teamData.employeeId}
            >
              <option value="">No manager</option>
              {selectable.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name} — {o.role}
                </option>
              ))}
            </select>
          </FieldBlock>
        </Row>

        <FieldBlock>
          <label htmlFor={`${mode}-subordinates`}>Team members</label>
          <Autocomplete
            multiple
            size="small"
            id={`${mode}-subordinates`}
            options={selectable}
            disabled={!teamData.employeeId}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={teamData.subordinates}
            onChange={(_, value) =>
              setTeamData((prev) => ({ ...prev, subordinates: value }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder={
                  teamData.subordinates.length ? "" : "Who reports to them"
                }
              />
            )}
            sx={{
              "& .MuiOutlinedInput-root": {
                background: colors.white,
                borderRadius: "8px",
                minHeight: "44px",
                fontSize: "0.86rem",
              },
            }}
          />
          <p className="hint">
            Leave either field empty to change only the other one. Empty values
            are not sent, so an existing manager or team stays as it is.
          </p>
        </FieldBlock>
      </Body>

      <Foot>
        <PrimaryButton type="submit" disabled={!canSave || isSaving}>
          {isSaving
            ? isCreate
              ? "Creating…"
              : "Updating…"
            : isCreate
            ? "Create"
            : "Update"}
        </PrimaryButton>
      </Foot>
    </Card>
  );
};

const Body = styled.div`
  padding: 16px 18px 4px 18px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 0.75rem;
`;

const FieldBlock = styled.div`
  margin-bottom: 14px;

  label {
    display: block;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: ${colors.textLight};
    margin-bottom: 6px;
  }
  select {
    width: 100%;
    font-family: inherit;
    font-size: 0.86rem;
    padding: 10px 12px;
    min-height: 44px;
    border-radius: 8px;
    border: 1px solid ${colors.inputBorderWhite};
    background: ${colors.white};
    color: ${colors.text};
  }
  select:disabled {
    background: ${colors.lightWhite};
    color: ${colors.textLight};
    cursor: not-allowed;
  }
  select:focus-visible {
    outline: 2px solid ${colors.textBlue};
    outline-offset: -1px;
  }
  .hint {
    margin: 6px 0 0 0;
    font-size: 0.75rem;
    line-height: 1.45;
    color: ${colors.textLight};
  }
`;

const Foot = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 13px 18px;
  border-top: 1px solid ${colors.inputBorderWhite};
  background: ${colors.lightWhite};
`;
